import { Card, Col, Row, Typography } from 'antd'
import { format } from 'date-fns'
import { Dayjs } from 'dayjs'
import { useVisualReportOps } from 'pages/reporting/reports/externalVisual/VisualReportProvider'
import { SignoffSection } from 'pages/reporting/shared/components/SignOffSection'
import { ReviewSection, ReviewSectionProps } from 'pages/reporting/shared/types'
import React, { useMemo } from 'react'
import styled from 'styled-components'

const Text = Typography.Text

const PageSection = styled(Card)`
  margin-bottom: 16px;
`

const SectionContent = styled.div`
  margin-left: 16px;
`

const DataRow = styled.div`
  margin-bottom: 8px;
  display: flex;
  gap: 8px;
`

type DateValue = Dayjs | string | Date | undefined | null

const formatDate = (date: DateValue) => {
  if (!date) return 'Not specified'

  if (date instanceof Date) {
    return format(date, 'MM/dd/yyyy')
  }

  if (typeof date === 'string') {
    return format(new Date(date), 'MM/dd/yyyy')
  }

  if (date.toDate && typeof date.toDate === 'function') {
    return format(date.toDate(), 'MM/dd/yyyy')
  }

  return 'Invalid date'
}

const RenderReact: React.FC<ReviewSectionProps> = ({ previewMode }) => {
  const {
    formData,
    handleSignoff,
    selectedRevisionData,
    isLoading,
    lockState,
    handleStartSignoff,
    handleUnlock,
    metadataEntryId,
    selectedVisualInspection,
    sectionResponses,
    fieldResponses,
  } = useVisualReportOps()

  const selectedFormData = useMemo(
    () =>
      previewMode && selectedRevisionData ? selectedRevisionData : formData,
    [formData, selectedRevisionData, previewMode]
  )

  const {
    coverSectionData,
    checklistFindingsData,
    assetCorrosionAnalysisData,
    findingsData,
    recommendationsData,
    pressureTestSubInspectionsData,
    bundleSubInspectionsData,
    inspectionTeamMemberData,
    referenceDocumentsData,
    signoff,
    revisionMetadata,
  } = selectedFormData

  // Get signature dates for display
  const supervisorSignDate = signoff?.supervisorSignoff?.timestamp
    ? new Date(signoff.supervisorSignoff.timestamp)
    : undefined

  const reviewerSignDate = signoff?.reviewerSignoff?.timestamp
    ? new Date(signoff.reviewerSignoff.timestamp)
    : undefined

  // Process checklist findings for display
  const checklistFindings = useMemo(() => {
    const findings: Array<{
      category: string
      name: string
      value: string
      findingSummary: string
    }> = []

    // Group field responses by section
    const fieldsBySection = fieldResponses.reduce(
      (acc, field) => {
        const sectionId = field.tags['visual-inspection-section-response']
        if (!acc[sectionId]) {
          acc[sectionId] = []
        }
        acc[sectionId].push(field)
        return acc
      },
      {} as Record<string, typeof fieldResponses>
    )

    // Process each section
    sectionResponses.forEach((section) => {
      const sectionFields = fieldsBySection[section.id] || []

      // Sort fields by order
      sectionFields.sort((a, b) => a.data.order - b.data.order)

      sectionFields.forEach((field) => {
        findings.push({
          category: section.data.title,
          name: field.data.label,
          value: String(field.data.value || 'N/A'),
          findingSummary: field.data.notes.join(' ') || '',
        })
      })
    })

    return findings
  }, [sectionResponses, fieldResponses])

  return (
    <div>
      <PageSection title='Cover Information'>
        <SectionContent>
          <DataRow>
            <Text strong>Asset:</Text> {coverSectionData.asset}
          </DataRow>
          <DataRow>
            <Text strong>Asset ID:</Text> {coverSectionData.assetId}
          </DataRow>
          {coverSectionData.circuitId && (
            <DataRow>
              <Text strong>Circuit ID:</Text> {coverSectionData.circuitId}
            </DataRow>
          )}
          <DataRow>
            <Text strong>Inspection Type:</Text>{' '}
            {coverSectionData.inspectionType}
          </DataRow>
          {coverSectionData.degradationMechanism && (
            <DataRow>
              <Text strong>Degradation Mechanism:</Text>{' '}
              {coverSectionData.degradationMechanism}
            </DataRow>
          )}
          {coverSectionData.extent && (
            <DataRow>
              <Text strong>Extent:</Text> {coverSectionData.extent}
            </DataRow>
          )}
          {coverSectionData.actualWorkTime && (
            <DataRow>
              <Text strong>Actual Work Time:</Text>{' '}
              {coverSectionData.actualWorkTime}
            </DataRow>
          )}
          <DataRow>
            <Text strong>Inspection Date:</Text>{' '}
            {selectedVisualInspection
              ? formatDate(selectedVisualInspection.data.date)
              : 'Not specified'}
          </DataRow>
          <DataRow>
            <Text strong>Revision Number:</Text>{' '}
            {revisionMetadata.revisionNumber}
          </DataRow>
          <DataRow>
            <Text strong>Publication Status:</Text>{' '}
            {revisionMetadata.isPublished ? 'Published' : 'Draft'}
          </DataRow>

          {/* Display supervisor information from signoff */}
          <DataRow>
            <Text strong>Inspection Supervisor:</Text>{' '}
            {signoff?.supervisorSignoff?.name || 'Not signed'}
          </DataRow>
          {signoff?.supervisorSignoff && (
            <>
              <DataRow>
                <Text strong>Supervisor Qualifications:</Text>{' '}
                {signoff.supervisorSignoff.qualifications}
              </DataRow>
              <DataRow>
                <Text strong>Supervisor Signoff Date:</Text>{' '}
                {formatDate(supervisorSignDate)}
              </DataRow>
            </>
          )}

          {/* Display reviewer information from signoff */}
          <DataRow>
            <Text strong>Reviewer:</Text>{' '}
            {signoff?.reviewerSignoff?.name || 'Not signed'}
          </DataRow>
          {signoff?.reviewerSignoff && (
            <>
              <DataRow>
                <Text strong>Reviewer Qualifications:</Text>{' '}
                {signoff.reviewerSignoff.qualifications}
              </DataRow>
              <DataRow>
                <Text strong>Review Date:</Text> {formatDate(reviewerSignDate)}
              </DataRow>
            </>
          )}
        </SectionContent>
      </PageSection>

      <PageSection title='Inspection Summary'>
        <SectionContent>
          {coverSectionData.inspectionSummary ? (
            <DataRow>{coverSectionData.inspectionSummary}</DataRow>
          ) : (
            <DataRow>
              <Text type='secondary'>No inspection summary provided</Text>
            </DataRow>
          )}
        </SectionContent>
      </PageSection>

      <PageSection title='Checklist Findings'>
        <SectionContent>
          {checklistFindings.length > 0 ? (
            <>
              <DataRow>
                <Text strong>Total Findings:</Text> {checklistFindings.length}
              </DataRow>
              <div style={{ marginTop: 16 }}>
                <Row gutter={16} style={{ marginBottom: 8 }}>
                  <Col span={6}>
                    <Text strong>Category</Text>
                  </Col>
                  <Col span={6}>
                    <Text strong>Name</Text>
                  </Col>
                  <Col span={4}>
                    <Text strong>Value</Text>
                  </Col>
                  <Col span={8}>
                    <Text strong>Finding Summary</Text>
                  </Col>
                </Row>
                {checklistFindings.slice(0, 5).map((finding, index) => (
                  <Row key={index} gutter={16} style={{ marginBottom: 4 }}>
                    <Col span={6}>
                      <Text>{finding.category}</Text>
                    </Col>
                    <Col span={6}>
                      <Text>{finding.name}</Text>
                    </Col>
                    <Col span={4}>
                      <Text>{finding.value}</Text>
                    </Col>
                    <Col span={8}>
                      <Text>{finding.findingSummary || '-'}</Text>
                    </Col>
                  </Row>
                ))}
                {checklistFindings.length > 5 && (
                  <DataRow>
                    <Text type='secondary'>
                      ... and {checklistFindings.length - 5} more findings
                    </Text>
                  </DataRow>
                )}
              </div>
            </>
          ) : (
            <DataRow>
              <Text type='secondary'>No checklist findings available</Text>
            </DataRow>
          )}
          {checklistFindingsData.scope && (
            <DataRow style={{ marginTop: 16 }}>
              <Text strong>Scope:</Text> {checklistFindingsData.scope}
            </DataRow>
          )}
        </SectionContent>
      </PageSection>

      <PageSection title='Asset Corrosion Analysis'>
        <SectionContent>
          {assetCorrosionAnalysisData.analysis ? (
            <DataRow>{assetCorrosionAnalysisData.analysis}</DataRow>
          ) : (
            <DataRow>
              <Text type='secondary'>No corrosion analysis provided</Text>
            </DataRow>
          )}
        </SectionContent>
      </PageSection>

      <PageSection title='Findings'>
        <SectionContent>
          {findingsData.findings ? (
            <DataRow>{findingsData.findings}</DataRow>
          ) : (
            <DataRow>
              <Text type='secondary'>No findings provided</Text>
            </DataRow>
          )}
        </SectionContent>
      </PageSection>

      <PageSection title='Recommendations'>
        <SectionContent>
          {recommendationsData.recommendations &&
          recommendationsData.recommendations.length > 0 ? (
            <>
              <DataRow>
                <Text strong>Total Recommendations:</Text>{' '}
                {recommendationsData.recommendations.length}
              </DataRow>
              {recommendationsData.recommendations.map((rec) => (
                <div
                  key={rec.recommendationId}
                  style={{
                    marginTop: 16,
                    padding: 12,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 4,
                  }}>
                  <DataRow>
                    <Text strong>ID:</Text> REC-{rec.recommendationId}
                  </DataRow>
                  <DataRow>
                    <Text strong>Headline:</Text> {rec.headline}
                  </DataRow>
                  <DataRow>
                    <Text strong>Description:</Text> {rec.description}
                  </DataRow>
                  <DataRow>
                    <Text strong>Target Completion Date:</Text>{' '}
                    {formatDate(rec.targetCompletionDate)}
                  </DataRow>
                  <DataRow>
                    <Text strong>Author:</Text> {rec.author}
                  </DataRow>
                </div>
              ))}
            </>
          ) : (
            <DataRow>
              <Text type='secondary'>No recommendations provided</Text>
            </DataRow>
          )}
        </SectionContent>
      </PageSection>

      <PageSection title='Sub-Inspections'>
        <SectionContent>
          <DataRow>
            <Text strong>Pressure Test Sub-Inspections:</Text>{' '}
            {pressureTestSubInspectionsData.pressureTestSubInspection || 'None'}
          </DataRow>
          <DataRow>
            <Text strong>Bundle Sub-Inspections:</Text>{' '}
            {bundleSubInspectionsData.bundleSubInspection || 'None'}
          </DataRow>
        </SectionContent>
      </PageSection>

      <PageSection title='Inspection Team'>
        <SectionContent>
          {inspectionTeamMemberData.inspectionTeamMembers &&
          inspectionTeamMemberData.inspectionTeamMembers.length > 0 ? (
            <>
              <DataRow>
                <Text strong>Team Members:</Text>{' '}
                {inspectionTeamMemberData.inspectionTeamMembers.length}
              </DataRow>
              <div style={{ marginTop: 16 }}>
                <Row gutter={16} style={{ marginBottom: 8 }}>
                  <Col span={6}>
                    <Text strong>Name</Text>
                  </Col>
                  <Col span={6}>
                    <Text strong>Certification</Text>
                  </Col>
                  <Col span={6}>
                    <Text strong>Certification ID</Text>
                  </Col>
                  <Col span={6}>
                    <Text strong>Expiration Date</Text>
                  </Col>
                </Row>
                {inspectionTeamMemberData.inspectionTeamMembers.map(
                  (member, index) => (
                    <Row key={index} gutter={16} style={{ marginBottom: 4 }}>
                      <Col span={6}>
                        <Text>{member.name}</Text>
                      </Col>
                      <Col span={6}>
                        <Text>{member.certification.name}</Text>
                      </Col>
                      <Col span={6}>
                        <Text>{member.certification.id}</Text>
                      </Col>
                      <Col span={6}>
                        <Text>
                          {formatDate(member.certification.expirationDate)}
                        </Text>
                      </Col>
                    </Row>
                  )
                )}
              </div>
            </>
          ) : (
            <DataRow>
              <Text type='secondary'>No team members specified</Text>
            </DataRow>
          )}
        </SectionContent>
      </PageSection>

      <PageSection title='Reference Documents'>
        <SectionContent>
          {referenceDocumentsData.referenceDocuments &&
          referenceDocumentsData.referenceDocuments.length > 0 ? (
            <>
              <DataRow>
                <Text strong>Total Documents:</Text>{' '}
                {referenceDocumentsData.referenceDocuments.length}
              </DataRow>
              {referenceDocumentsData.referenceDocuments.map((doc, index) => (
                <DataRow key={doc.id} style={{ marginTop: 8 }}>
                  <Text>
                    ({index + 1}) {doc.description} - {doc.path}
                  </Text>
                </DataRow>
              ))}
            </>
          ) : (
            <DataRow>
              <Text type='secondary'>No reference documents specified</Text>
            </DataRow>
          )}
        </SectionContent>
      </PageSection>

      {/* Only show SignoffSection if not in preview mode */}
      {!previewMode && (
        <SignoffSection
          currentSignoff={signoff}
          onSignoff={handleSignoff}
          loading={isLoading}
          isLocked={lockState.isLocked}
          onStartSignoff={handleStartSignoff}
          onUnlock={handleUnlock}
          publishedPdfPath={formData.publishedPdfPath}
          metadataEntryId={metadataEntryId}
        />
      )}

      {/* If in preview mode and has signoff, show read-only signoff info */}
      {previewMode && signoff && (
        <PageSection title='Signoff Information'>
          <SectionContent>
            {signoff.supervisorSignoff && (
              <div style={{ marginBottom: '16px' }}>
                <Text strong>Supervisor Signoff</Text>
                <DataRow>
                  <Text>Name:</Text> {signoff.supervisorSignoff.name}
                </DataRow>
                <DataRow>
                  <Text>Email:</Text> {signoff.supervisorSignoff.email}
                </DataRow>
                <DataRow>
                  <Text>Qualifications:</Text>{' '}
                  {signoff.supervisorSignoff.qualifications}
                </DataRow>
                <DataRow>
                  <Text>Date:</Text>{' '}
                  {format(
                    new Date(signoff.supervisorSignoff.timestamp),
                    'MMM dd, yyyy HH:mm:ss'
                  )}
                </DataRow>
              </div>
            )}
            {signoff.reviewerSignoff && (
              <div>
                <Text strong>Reviewer Signoff</Text>
                <DataRow>
                  <Text>Name:</Text> {signoff.reviewerSignoff.name}
                </DataRow>
                <DataRow>
                  <Text>Email:</Text> {signoff.reviewerSignoff.email}
                </DataRow>
                <DataRow>
                  <Text>Qualifications:</Text>{' '}
                  {signoff.reviewerSignoff.qualifications}
                </DataRow>
                <DataRow>
                  <Text>Date:</Text>{' '}
                  {format(
                    new Date(signoff.reviewerSignoff.timestamp),
                    'MMM dd, yyyy HH:mm:ss'
                  )}
                </DataRow>
              </div>
            )}
          </SectionContent>
        </PageSection>
      )}
    </div>
  )
}

export const ReportReview: ReviewSection = {
  id: 'review',
  title: 'Review',
  description: 'Review report data and complete sign off',
  RenderReact,
  required: true,
}
